import { useEffect, useRef } from "react";
// @ts-ignore — formiojs v4 ships without first-class TS types
import { Formio } from "formiojs";

/**
 * FormioRenderer — direct-mount wrapper around formiojs v4 forms.
 *
 * We deliberately avoid `@formio/react`'s `<Form>` wrapper here. With the
 * `@formio/js@5` + `@formio/react@6` stack the FormBuilder freeze bug
 * (component-edit dialog Save/Cancel buttons doing nothing) also leaked
 * into the renderer in unpredictable ways. This wrapper has no React
 * reactivity inside the FormIO subtree — React owns only the host
 * element; the form instance owns everything inside.
 *
 * Re-mount triggers (effect deps):
 *   - `form` schema reference change
 *   - any option change is opaque to React; pass a stable `options` ref
 *     if you want option changes to take effect without remounting.
 *
 * Submission-data push: when `submission` changes, we call
 * `instance.submission = submission` rather than re-mounting, so the
 * user's in-flight edits aren't lost on every prop tick.
 */
export interface FormioRendererProps {
  /** Form.io schema object. */
  form: any;
  /** Initial / pushed submission data, e.g. `{ data: { ... } }`. */
  submission?: any;
  /** Called when the user submits the form. */
  onSubmit?: (submission: any) => void;
  /** Called once with the form instance after `formReady`. */
  formReady?: (instance: any) => void;
  /** Formio options object. Treated as static — change `key` to remount. */
  options?: any;
  /** Optional class name applied to the host wrapper. */
  className?: string;
}

export default function FormioRenderer({
  form,
  submission,
  onSubmit,
  formReady,
  options,
  className,
}: FormioRendererProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<any>(null);

  // Mount the form whenever the schema reference changes. Submission
  // data is pushed via the secondary effect below — pushing it through
  // `setSubmission` rather than remount preserves user input.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !form) return;
    host.innerHTML = "";

    let cancelled = false;
    let instance: any = null;

    (Formio as any)
      .createForm(host, form, options || {})
      .then((created: any) => {
        if (cancelled) {
          try {
            created?.destroy?.(true);
          } catch {}
          return;
        }
        instance = created;
        instanceRef.current = created;
        if (submission != null) {
          try {
            created.submission = submission;
          } catch (err) {
            console.error("[FormioRenderer] failed to seed submission", err);
          }
        }
        if (onSubmit) {
          created.on("submit", (sub: any) => onSubmit(sub));
        }
        formReady?.(created);
      })
      .catch((err: unknown) => {
        console.error("[FormioRenderer] createForm failed", err);
      });

    return () => {
      cancelled = true;
      try {
        instance?.destroy?.(true);
      } catch {}
      instanceRef.current = null;
      if (host) host.innerHTML = "";
    };
    // We intentionally re-mount on `form` schema change. Submission
    // changes are handled by the next effect, not here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // Push submission data into the live instance when it changes.
  useEffect(() => {
    const inst = instanceRef.current;
    if (!inst || submission == null) return;
    try {
      inst.submission = submission;
    } catch (err) {
      console.error("[FormioRenderer] submission push error", err);
    }
  }, [submission]);

  return <div ref={hostRef} className={className} />;
}
